/**
 * AIActivityCard — Dashboard sidebar card showing recent AI activity for a case.
 *
 * Compact summary: agent badges, last few actions, quick stats.
 * Designed for the AccountantDashboard sidebar alongside other insight cards.
 */

import React, { useState, useEffect } from 'react';
import { Brain, RefreshCw, AlertCircle, Clock, ChevronRight } from 'lucide-react';
import { getCaseAIActivity, getCaseAISummary, type AIActivityEntry, type AIActivitySummary } from '../../services/aiActivityApi';

interface AIActivityCardProps {
  reportId?: string;
  className?: string;
  /**
   * Pre-fetched slice from /dashboard/case-details. When provided the
   * widget renders from the slice and skips its two /ai-activity calls.
   */
  data?: {
    entries?: AIActivityEntry[];
    summary?: AIActivitySummary;
    error?: string;
  } | null;
  isLoading?: boolean;
}

const AGENT_DOT: Record<string, string> = {
  'Report Narrator': 'var(--color-brand)',
  'Intelligence Agent': 'var(--color-brand)',
  'Forensic Analyzer': 'var(--color-brand)',
  'Settlement Calculator': 'var(--color-verified)',
  'Future Closure Engine': 'var(--color-brand)',
  'Edge Case Narrator': 'var(--color-brand)',
};

function timeAgo(iso: string): string {
  try {
    const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffD = Math.floor(diffHr / 24);
    return `${diffD}d ago`;
  } catch {
    return '';
  }
}

export default function AIActivityCard({
  reportId,
  className = '',
  data: sliceData,
  isLoading: externalLoading,
}: AIActivityCardProps) {
  const [entries, setEntries] = useState<AIActivityEntry[]>([]);
  const [summary, setSummary] = useState<AIActivitySummary | null>(null);
  const [loading, setLoading] = useState(false);

  const batchedMode = sliceData !== undefined;

  useEffect(() => {
    if (!batchedMode) return;
    if (!sliceData || sliceData.error) {
      setEntries([]);
      setSummary(null);
      return;
    }
    setEntries(sliceData.entries || []);
    setSummary(sliceData.summary || null);
  }, [batchedMode, sliceData]);

  useEffect(() => {
    if (batchedMode) return; // parent owns the fetch
    if (!reportId) {
      setEntries([]);
      setSummary(null);
      return;
    }
    setLoading(true);
    Promise.all([
      getCaseAIActivity(reportId, { limit: 5 }),
      getCaseAISummary(reportId),
    ])
      .then(([act, sum]) => {
        setEntries(act);
        setSummary(sum);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [reportId, batchedMode]);

  const effectiveLoading = batchedMode ? !!externalLoading : loading;

  // Empty state — no case selected
  if (!reportId) {
    return (
      <div className={`rounded-xl border shadow-sm p-5 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <Brain className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>AI Activity</h3>
        </div>
        <p className="text-xs text-center py-4" style={{ color: 'var(--color-text-dim)' }}>Select a case to view AI activity</p>
      </div>
    );
  }

  // Loading
  if (effectiveLoading && entries.length === 0) {
    return (
      <div className={`rounded-xl border shadow-sm p-5 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <Brain className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>AI Activity</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
          <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Loading...</span>
        </div>
      </div>
    );
  }

  const agentNames = summary ? Object.keys(summary.agents) : [];

  return (
    <div className={`rounded-xl border shadow-sm p-5 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <Brain className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>AI Activity</h3>
            {summary && summary.total_interactions > 0 && (
              <p className="text-[10px]" style={{ color: 'var(--color-text-dim)' }}>{summary.total_interactions} total actions</p>
            )}
          </div>
        </div>
      </div>

      {/* Agent pills */}
      {agentNames.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {agentNames.map((name) => (
            <span key={name} className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-elevated)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: AGENT_DOT[name] || 'var(--color-text-dim)' }} />
              {name.split(' ').pop()} ({summary?.agents[name]?.count})
            </span>
          ))}
        </div>
      )}

      {/* Recent entries */}
      {entries.length === 0 ? (
        <p className="text-xs text-center py-3" style={{ color: 'var(--color-text-dim)' }}>No AI activity yet</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <div
              key={e.activity_id}
              className="flex items-start gap-2 text-xs rounded-md px-2 py-1.5"
              style={{ backgroundColor: e.status === 'error' ? 'var(--color-needs-human-tint)' : 'var(--color-bg-elevated)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: AGENT_DOT[e.agent_name] || 'var(--color-text-dim)' }} />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate" style={{ color: 'var(--color-text-secondary)' }}>{e.action}</p>
                <p className="truncate" style={{ color: 'var(--color-text-dim)' }}>{e.output_summary || e.input_summary}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }}>
                {e.status === 'error' && <AlertCircle className="w-3 h-3" style={{ color: 'var(--color-needs-human-text)' }} />}
                <Clock className="w-3 h-3" />
                <span>{timeAgo(e.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
